import { NavLink, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  Wrench, 
  Package, 
  DollarSign, 
  Truck, 
  Settings,
  LogOut,
  Crown,
  Lock,
  User
} from 'lucide-react';

// Removed Vehículos from menu - now accessed through Clientes
const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/clients', icon: Users, label: 'Clientes' },
  { to: '/services', icon: Wrench, label: 'Servicios' },
  { to: '/inventory', icon: Package, label: 'Inventario' },
  { to: '/finance', icon: DollarSign, label: 'Finanzas' },
  { to: '/suppliers', icon: Truck, label: 'Proveedores' },
  { to: '/profile', icon: Settings, label: 'Perfil' },
];

// Mobile navigation items (Dashboard, Clientes, Servicios, Proveedores)
const mobileNavItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Inicio' },
  { to: '/clients', icon: Users, label: 'Clientes' },
  { to: '/services', icon: Wrench, label: 'Servicios' },
  { to: '/suppliers', icon: Truck, label: 'Proveedores' },
];

export const Sidebar = () => {
  const { user, logout, isPro } = useAuth();
  const location = useLocation();

  return (
    <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-200 h-screen sticky top-0">
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          {user?.workshop_logo ? (
            <img 
              src={user.workshop_logo} 
              alt="Logo" 
              className="w-10 h-10 rounded-lg object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-[#1E3A5F] flex items-center justify-center">
              <Wrench className="w-5 h-5 text-white" />
            </div>
          )}
          <div>
            <h1 className="font-bold text-slate-900 truncate max-w-[140px]">
              {user?.workshop_name || 'TallerMec Pro'}
            </h1>
            {isPro ? (
              <span className="badge-pro inline-flex items-center gap-1">
                <Crown className="w-3 h-3" /> PRO
              </span>
            ) : (
              <span className="badge-free">GRATIS</span>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
          const isLocked = item.pro && !isPro;
          
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={`sidebar-link ${isActive ? 'sidebar-link-active' : ''} ${isLocked ? 'opacity-60' : ''}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{item.label}</span>
              {isLocked && <Lock className="w-4 h-4" />}
            </NavLink>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
              <span className="text-slate-600 font-medium">{user?.name?.[0]?.toUpperCase()}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </aside>
  );
};

export const MobileNav = () => {
  const location = useLocation();

  return (
    <nav className="mobile-nav safe-area-bottom">
      <div className="flex justify-around py-2 px-2">
        {mobileNavItems.map((item) => {
          const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
          
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={`mobile-nav-link ${isActive ? 'mobile-nav-link-active' : ''}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs mt-1">{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

// Header component with profile icon for mobile
export const MobileHeader = () => {
  const { user } = useAuth();
  
  return (
    <header className="md:hidden glass-header sticky top-0 z-40 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        {user?.workshop_logo ? (
          <img src={user.workshop_logo} alt="Logo" className="w-8 h-8 rounded-lg object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-[#1E3A5F] flex items-center justify-center">
            <Wrench className="w-4 h-4 text-white" />
          </div>
        )}
        <span className="font-bold text-slate-900 text-sm truncate max-w-[150px]">
          {user?.workshop_name || 'TallerMec Pro'}
        </span>
      </div>
      <Link 
        to="/profile" 
        className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
        data-testid="mobile-profile-btn"
      >
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full" />
        ) : (
          <User className="w-5 h-5 text-slate-600" />
        )}
      </Link>
    </header>
  );
};
